/**
 * Funções de cálculo financeiro puras (sem estado).
 * Paridade com SimuladorMapa.aspx.cs (V1).
 */

// ==================== PRICE ====================

/**
 * PRICE: Calcula valor da parcela dado saldo e quantidade.
 * V1 ref: SimuladorMapa.aspx.cs linhas 407-475
 */
export function calcularValorParcelaPRICE(saldoFinanciar, taxa, qtdParcelas) {
  if (qtdParcelas <= 0) return 0;
  if (qtdParcelas === 1) return saldoFinanciar;

  if (taxa > 0) {
    var jurosPorParcela = Math.pow(1 + taxa, -qtdParcelas);
    return Math.round((saldoFinanciar / ((1 - jurosPorParcela) / taxa)) * 100) / 100;
  }
  return Math.round((saldoFinanciar / qtdParcelas) * 100) / 100;
}

/**
 * PRICE: Calcula quantidade de parcelas dado saldo e valor da parcela.
 * V1 ref: SimuladorMapa.aspx.cs linhas 477-559
 */
export function calcularQtdParcelasPRICE(saldoFinanciar, taxa, valorParcela) {
  if (valorParcela <= 0 || saldoFinanciar <= 0) return 0;

  var qtd;
  if (taxa > 0) {
    var denominador = (taxa * saldoFinanciar) / valorParcela;
    if (denominador >= 1) return 0;
    qtd = Math.log(1 / (1 - denominador)) / Math.log(1 + taxa);
  } else {
    qtd = saldoFinanciar / valorParcela;
  }
  return qtd;
}

/**
 * PRICE: Calcula entrada dado qtd parcelas e valor da parcela.
 * V1 ref: SimuladorMapa.aspx.cs linhas 580-648
 */
export function calcularEntradaPRICE(valorVista, taxa, qtdParcelas, valorParcela) {
  if (qtdParcelas <= 0 || valorParcela <= 0) return 0;

  if (taxa > 0) {
    var coeficiente = taxa / (1 - (1 / Math.pow(1 + taxa, qtdParcelas)));
    return Math.round((valorVista - (valorParcela / coeficiente)) * 100) / 100;
  }
  return Math.round((valorVista - (valorParcela * qtdParcelas)) * 100) / 100;
}

// ==================== SAC ====================

/**
 * Calcula valor de uma parcela SAC no mês i.
 * V1 ref: SimuladorMapa.aspx.cs linhas 397-405
 */
export function calcularParcelaSAC(amortizacao, qtdParcelas, i, taxa) {
  var saldoRemanescente = amortizacao * (qtdParcelas - i);
  var juros = saldoRemanescente * taxa;
  return Math.round((amortizacao + juros) * 100) / 100;
}

/**
 * SAC: Gera tabela completa de amortização.
 * V1 ref: SimuladorMapa.aspx.cs linhas 913-976
 */
export function gerarTabelaSAC(saldoDevedor, taxa, qtdParcelas) {
  if (qtdParcelas <= 0 || saldoDevedor <= 0) return [];

  var amortizacao = saldoDevedor / qtdParcelas;
  var tabela = [];
  var saldo = saldoDevedor;

  for (var i = 0; i < qtdParcelas; i++) {
    var juros = saldo * taxa;
    var prestacao = amortizacao + juros;
    saldo -= amortizacao;

    tabela.push({
      mes: i + 1,
      prestacao: Math.round(prestacao * 100) / 100,
      amortizacao: Math.round(amortizacao * 100) / 100,
      juros: Math.round(juros * 100) / 100,
      saldoDevedor: Math.round(Math.max(saldo, 0) * 100) / 100,
    });
  }
  return tabela;
}

/**
 * SAC: Calcula quantidade de parcelas dado valor da parcela.
 * V1 ref: SimuladorMapa.aspx.cs linhas 493-500
 */
export function calcularQtdParcelasSAC(saldoFinanciar, taxa, valorParcela) {
  if (valorParcela <= 0 || saldoFinanciar <= 0) return 0;

  var denominador = valorParcela - (saldoFinanciar * taxa);
  if (denominador <= 0) return 0;
  return saldoFinanciar / denominador;
}

// ==================== ANTECIPAÇÃO ====================

/**
 * Calcula valor presente de uma parcela futura.
 * V1 ref: SimuladorMapa.aspx.cs linhas 386-395
 */
export function calcularValorPresente(valorParcela, jurosPorFora, meses) {
  if (meses <= 0) return valorParcela;
  var dias = meses * 30;
  return Math.round((valorParcela / Math.pow(1 + jurosPorFora / 100, dias / 30)) * 100) / 100;
}

/**
 * Gera tabela completa de antecipação + resumos top 5/10/15.
 * V1 ref: SimuladorMapa.aspx.cs linhas 978-1077
 */
export function gerarTabelaAntecipacao(valorParcela, jurosPorFora, qtdParcelas, saldoFinanciar) {
  var parcelas = [];
  var totalTop5 = 0;
  var totalTop10 = 0;
  var totalTop15 = 0;

  for (var i = 0; i < qtdParcelas; i++) {
    var meses = i;
    var vp = calcularValorPresente(valorParcela, jurosPorFora, meses);
    var vpArredondado = Math.round(vp * 100) / 100;

    parcelas.push({
      numero: i + 1,
      valorOriginal: valorParcela,
      valorPresente: vpArredondado,
    });

    if (i >= qtdParcelas - 5) totalTop5 += vpArredondado;
    if (i >= qtdParcelas - 10) totalTop10 += vpArredondado;
    if (i >= qtdParcelas - 15) totalTop15 += vpArredondado;
  }

  return {
    parcelas: parcelas,
    top5: Math.round(totalTop5 * 100) / 100,
    top10: Math.round(totalTop10 * 100) / 100,
    top15: Math.round(totalTop15 * 100) / 100,
  };
}

/**
 * Calcula antecipação de N últimas parcelas.
 */
export function calcularAntecipacaoUltimas(valorParcela, jurosPorFora, qtdTotal, qtdAntecipar, saldoFinanciar) {
  if (qtdAntecipar >= qtdTotal && saldoFinanciar) return saldoFinanciar;

  var total = 0;
  for (var i = qtdTotal - qtdAntecipar; i < qtdTotal; i++) {
    total += calcularValorPresente(valorParcela, jurosPorFora, i);
  }
  return Math.round(total * 100) / 100;
}

// ==================== ENTRADA ====================

/**
 * Processa e valida entrada do cliente.
 * V1 ref: SimuladorMapa.aspx.cs CarregarEntrada
 */
export function processarEntrada(entradaCliente, entradaImobiliaria, entradaIncorporadora, valorVista) {
  if (entradaCliente < entradaImobiliaria) {
    return {
      valido: false,
      erro: 'Entrada do cliente deve ser maior ou igual à entrada imobiliária (R$ ' +
        entradaImobiliaria.toFixed(2).replace('.', ',') + ').',
    };
  }

  var saldoFinanciar = Math.round((valorVista - entradaCliente) * 100) / 100;
  if (saldoFinanciar < 0) saldoFinanciar = 0;

  return {
    valido: true,
    entradaCliente: entradaCliente,
    saldoFinanciar: saldoFinanciar,
  };
}

/**
 * Ajusta resto de quantidade de parcelas (modo Q).
 * Quando a divisão não é exata, ajusta a entrada para compensar.
 * V1 ref: SimuladorMapa.aspx.cs linhas 530-558
 */
export function ajustarRestoQtdParcelas(qtdCalculada, valorParcela, saldoFinanciar, entradaAtual, valorVista) {
  var qtdInteira = Math.floor(qtdCalculada);
  var resto = qtdCalculada - qtdInteira;

  if (resto <= 0) {
    return { qtdParcelas: qtdInteira, entradaAlterada: false, novaEntrada: entradaAtual };
  }

  var saldoResto = resto * valorParcela;
  var novaEntrada = entradaAtual + saldoResto;

  return {
    qtdParcelas: qtdInteira,
    entradaAlterada: true,
    novaEntrada: Math.round(novaEntrada * 100) / 100,
  };
}
